import { FunctionComponent } from 'react'
import Dialog from '@/components/_custom/Dialog/Dialog'
import { useTranslation } from 'react-i18next'
import DialogHeader from '@/components/_custom/DialogHeader/DialogHeader'
import DialogBody from '@/components/_custom/DialogBody/DialogBody'
import styles from './confirmDeactiveAccountDialog.module.scss'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import { Check, Close } from '@mui/icons-material'
import { useMutation } from '@apollo/client'
import { DEACTIVATE_EMAIL_ACCOUNT } from '@/modules/API/software/emailHandler'
import { EmailUser } from '@/types/EmailHandlerType'
import EmailData from '../../emailData'
import { useNotification } from '@/modules/context/NotificationContext.tsx'

interface ConfirmDeactiveAccountEmailDialogProps {
    open: boolean
    handleClose: () => void
    email: string
    setEmail: (email: string) => void
    emailDeactivationData?: EmailUser
    setEmailDeactivationData: (data: EmailUser | undefined) => void
}

const ConfirmDeactiveAccountEmailDialog: FunctionComponent<ConfirmDeactiveAccountEmailDialogProps> = ({
    open,
    handleClose,
    email,
    setEmail,
    emailDeactivationData,
    setEmailDeactivationData
}: ConfirmDeactiveAccountEmailDialogProps) => {
    const { t } = useTranslation()

    const { showNotificationSuccess, showNotificationError } = useNotification()

    const [deactivateEmail] = useMutation(DEACTIVATE_EMAIL_ACCOUNT, {
        variables: {
            email: email
        },
        onCompleted: () => {
            showNotificationSuccess(t('deactiveAccountSuccess'))

            setEmail('')
            setEmailDeactivationData(undefined)

            handleClose()
        },
        onError: () => {
            showNotificationError(t('deactiveAccountError'))
            handleClose()
        }
    })

    return (
        <Dialog open={open} handleClose={handleClose} className={styles.Dialog}>
            <DialogHeader title={t('deactivateAccount')} />
            <DialogBody className={styles.DialogBody}>
                <p className={styles.Text}>{t('deactivateAccountMessage')}</p>
                <div className={styles.Text}>
                    <span className={styles.TextEmail}> {email} </span>
                    <EmailData data={emailDeactivationData} loading={false} />
                </div>
                <div className={styles.ButtonContainer}>
                    <ButtonTextIcon
                        type={'custom'}
                        customText={t('yes')}
                        customIcon={<Check />}
                        onClick={deactivateEmail}
                        className={styles.Button}
                    />
                    <ButtonTextIcon
                        type={'custom'}
                        customText={t('no')}
                        customIcon={<Close />}
                        onClick={handleClose}
                        className={styles.Button}
                    />
                </div>
            </DialogBody>
        </Dialog>
    )
}

export default ConfirmDeactiveAccountEmailDialog
